/**
 * ContextRetriever — the shared client-side retrieval engine for the whole
 * intelligence layer (Digest, Catch Me Up, Ask Vector, Experts, Overlap).
 *
 * Responsibilities:
 *   1. LOAD a {@link EntitySnapshot} — every entity from batches 1–4 — through
 *      the existing feature stores (which each call the api facade only). No new
 *      backend, no new collection: just the generic-store reads the app already
 *      does elsewhere.
 *   2. PROJECT each entity into a uniform {@link EntityRef} (title, route,
 *      timestamp, people, systems, searchable text) so downstream code can
 *      treat every kind the same and link answers back to the exact entity.
 *   3. FILTER refs by time window, RANK refs by free-text relevance (RAG-lite),
 *      and FORMAT a compact, bounded context block for the model.
 *
 * Strict layering: this is a feature-level module. It talks to the API ONLY via
 * the feature stores (which use the facade); it never imports ApiClient or
 * calls fetch. React binds to it through {@link ./useContextSnapshot}.
 *
 * Pure retrieval — no productivity/velocity/ranking of people anywhere.
 */

import type { EngineeringVectorApi } from '../../api';
import { GoalStore } from '../goals/goalStore';
import { WorkstreamStore } from '../workstreams/workstreamStore';
import { ChangeStore } from '../changes/changeStore';
import { DailyUpdateStore } from '../dailyUpdates/dailyUpdateStore';
import { BlockerStore } from '../blockers/blockerStore';
import { TeamDocumentStore } from '../documents/documentStore';
import { DecisionStore } from '../decisions/decisionStore';
import { ServiceStore } from '../services/serviceStore';
import { TaskStore } from '../tasks/taskStore';
import { EstimateStore } from '../estimation/estimateStore';
import { SprintStore } from '../sprintboard/stores/sprintStore';
import { TaskUpdateStore } from '../sprintboard/stores/taskUpdateStore';
import { normalizeToIso, scoreText } from './intelligenceModel';
import type { EntityKind, EntityRef, EntitySnapshot } from './types';

/** Drops blanks + dedupes a list of people/system tokens, preserving order. */
function clean(values: (string | undefined)[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const t = (v ?? '').trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/** Best ISO timestamp from a list of candidate date strings (first parseable). */
function bestIso(...candidates: (string | undefined)[]): string {
  for (const c of candidates) {
    const iso = normalizeToIso(c);
    if (iso) return iso;
  }
  return '';
}

/** enc — route-safe id. */
const enc = (id: string) => encodeURIComponent(id);

export class ContextRetriever {
  private readonly goals: GoalStore;
  private readonly workstreams: WorkstreamStore;
  private readonly changes: ChangeStore;
  private readonly dailyUpdates: DailyUpdateStore;
  private readonly blockers: BlockerStore;
  private readonly documents: TeamDocumentStore;
  private readonly decisions: DecisionStore;
  private readonly services: ServiceStore;
  private readonly tasks: TaskStore;
  private readonly estimates: EstimateStore;
  private readonly sprints: SprintStore;
  private readonly taskUpdates: TaskUpdateStore;

  constructor(private readonly api: EngineeringVectorApi) {
    this.goals = new GoalStore(api);
    this.workstreams = new WorkstreamStore(api);
    this.changes = new ChangeStore(api);
    this.dailyUpdates = new DailyUpdateStore(api);
    this.blockers = new BlockerStore(api);
    this.documents = new TeamDocumentStore(api);
    this.decisions = new DecisionStore(api);
    this.services = new ServiceStore(api);
    this.tasks = new TaskStore(api);
    this.estimates = new EstimateStore(api);
    this.sprints = new SprintStore(api);
    this.taskUpdates = new TaskUpdateStore(api);
  }

  /**
   * Loads a full snapshot of every consumed entity, in parallel. Each list is
   * already non-deleted + collection-scoped by its store.
   */
  async loadSnapshot(): Promise<EntitySnapshot> {
    const [
      goals,
      workstreams,
      changes,
      dailyUpdates,
      blockers,
      documents,
      decisions,
      services,
      tasks,
      estimates,
      sprints,
      taskUpdates,
      identity,
    ] = await Promise.all([
      this.goals.listSummaries(),
      this.workstreams.listSummaries(),
      this.changes.listSummaries(),
      this.dailyUpdates.listSummaries(),
      this.blockers.listSummaries(),
      this.documents.listSummaries(),
      this.decisions.listSummaries(),
      this.services.listSummaries(),
      this.tasks.listAll(),
      this.estimates.listSummaries(),
      this.sprints.listAll(),
      this.taskUpdates.listAll(),
      this.api.identityProvider.getIdentity(),
    ]);
    return {
      goals,
      workstreams,
      changes,
      dailyUpdates,
      blockers,
      documents,
      decisions,
      services,
      tasks,
      estimates,
      sprints,
      taskUpdates,
      currentAlias: identity.alias,
      loadedAt: new Date().toISOString(),
    };
  }
}

/**
 * Projects a full snapshot into a flat, uniform list of {@link EntityRef}s —
 * the currency the retriever + all four surfaces operate on.
 */
export function toEntityRefs(snapshot: EntitySnapshot): EntityRef[] {
  const refs: EntityRef[] = [];

  for (const g of snapshot.goals) {
    refs.push({
      kind: 'goal',
      id: g.id,
      title: g.title || 'Untitled goal',
      route: `/goals/${enc(g.id)}`,
      timestamp: bestIso(g.lastUpdated),
      people: clean([g.goalLead, g.leadershipSponsor, g.owningTeam]),
      systems: [],
      text: clean([
        g.title,
        g.goalType,
        g.goalFramework,
        g.planningPeriod,
        `status ${g.status}`,
        g.owningTeam,
        g.goalLead,
        g.manualProgress !== undefined ? `progress ${g.manualProgress}%` : '',
      ]).join(' · '),
    });
  }

  for (const w of snapshot.workstreams) {
    refs.push({
      kind: 'workstream',
      id: w.id,
      title: w.title || 'Untitled workstream',
      route: `/workstreams/${enc(w.id)}`,
      timestamp: bestIso(w.lastUpdated),
      people: clean([w.engineer, w.poc, ...(w.followers ?? [])]),
      systems: clean(w.systemsAffected ?? []),
      text: clean([
        w.title,
        `engineer ${w.engineer}`,
        `stage ${w.currentStage}`,
        ...(w.systemsAffected ?? []),
      ]).join(' · '),
    });
  }

  for (const c of snapshot.changes) {
    refs.push({
      kind: 'change',
      id: c.id,
      title: c.title || 'Untitled change',
      route: '/changes',
      timestamp: bestIso(c.createdAt),
      people: clean([c.engineer]),
      systems: [],
      text: clean([c.title, c.changed, `why ${c.why}`, `impact ${c.impact}`]).join(
        ' · ',
      ),
    });
  }

  for (const d of snapshot.dailyUpdates) {
    const headline = d.today || d.yesterday || d.resultLearning || d.blocker || '';
    refs.push({
      kind: 'dailyUpdate',
      id: d.id,
      title: `${d.engineer || 'Update'} — ${d.date}`,
      route: '/daily-updates',
      timestamp: bestIso(d.createdAt, d.date),
      people: clean([d.engineer]),
      systems: [],
      text: clean([
        headline,
        d.yesterday,
        d.today,
        d.resultLearning,
        d.blocker ? `blocker ${d.blocker}` : '',
      ]).join(' · '),
    });
  }

  for (const b of snapshot.blockers) {
    refs.push({
      kind: 'blocker',
      id: b.id,
      title: b.summary || 'Blocker',
      route: '/blockers',
      timestamp: bestIso(b.lastUpdated, b.createdAt, b.blockingSince),
      people: clean([b.blockedEngineer, b.dependencyOwner, b.escalationOwner]),
      systems: [],
      text: clean([
        b.summary,
        `status ${b.status}`,
        `${b.internalExternal.toLowerCase()} dependency`,
        `owner ${b.dependencyOwner}`,
        b.forecastImpactDays !== undefined
          ? `forecast impact ${b.forecastImpactDays}d`
          : '',
      ]).join(' · '),
    });
  }

  for (const doc of snapshot.documents) {
    refs.push({
      kind: 'document',
      id: doc.id,
      title: doc.title || 'Untitled document',
      route: `/documents/${enc(doc.id)}`,
      timestamp: bestIso(doc.lastUpdated, doc.createdAt, doc.createdDate),
      people: clean([doc.ownerPoc, ...(doc.authors ?? []), ...(doc.reviewers ?? [])]),
      systems: clean([...(doc.relatedServices ?? []), ...(doc.tags ?? [])]),
      text: clean([
        doc.title,
        doc.docType,
        doc.summary,
        doc.contextWhy,
        ...(doc.tags ?? []),
        ...(doc.relatedServices ?? []),
        `status ${doc.status}`,
      ]).join(' · '),
    });
  }

  for (const dec of snapshot.decisions) {
    refs.push({
      kind: 'decision',
      id: dec.id,
      title: dec.decision || 'Decision',
      route: `/decisions/${enc(dec.id)}`,
      timestamp: bestIso(dec.lastUpdated, dec.createdAt, dec.date),
      people: clean([dec.owner, ...(dec.participants ?? [])]),
      systems: clean(dec.affectedServices ?? []),
      text: clean([
        dec.decision,
        dec.context,
        `chose ${dec.decisionMade}`,
        `why ${dec.why}`,
        ...(dec.affectedServices ?? []),
      ]).join(' · '),
    });
  }

  for (const s of snapshot.services) {
    refs.push({
      kind: 'service',
      id: s.id,
      title: s.name || 'Service',
      route: `/services/${enc(s.id)}`,
      timestamp: bestIso(s.lastUpdated, s.createdAt),
      people: clean([s.primaryPoc, s.secondaryPoc, s.owningTeam]),
      systems: clean([s.name, ...(s.repositories ?? []), ...(s.knownDependencies ?? [])]),
      text: clean([
        s.name,
        s.purpose,
        `team ${s.owningTeam}`,
        ...(s.repositories ?? []),
        ...(s.knownDependencies ?? []),
      ]).join(' · '),
    });
  }

  for (const t of snapshot.tasks) {
    refs.push({
      kind: 'story',
      id: t.id,
      title: t.title || 'Story',
      route: '/tasks',
      timestamp: bestIso(t.updatedAt),
      people: clean([t.assignee]),
      systems: clean(t.tags ?? []),
      text: clean([
        t.title,
        t.description,
        `status ${t.status}`,
        ...(t.tags ?? []),
      ]).join(' · '),
    });
  }

  for (const e of snapshot.estimates) {
    refs.push({
      kind: 'estimate',
      id: e.id,
      title: e.title || 'Estimate',
      route: `/estimation/${enc(e.id)}`,
      timestamp: bestIso(e.createdAt),
      people: clean([e.createdBy]),
      systems: [],
      text: clean([e.title, `template ${e.templateId}`]).join(' · '),
    });
  }

  return refs;
}

/** Human label for an entity kind (used in formatted context + source lists). */
export function kindLabel(kind: EntityKind): string {
  switch (kind) {
    case 'goal':
      return 'Goal';
    case 'workstream':
      return 'Workstream';
    case 'change':
      return 'Change';
    case 'dailyUpdate':
      return 'Daily Update';
    case 'blocker':
      return 'Blocker';
    case 'document':
      return 'Document';
    case 'decision':
      return 'Decision';
    case 'service':
      return 'Service';
    case 'story':
      return 'Story';
    case 'estimate':
      return 'Estimate';
  }
}

/** Section ordering used when formatting context / grouping sources. */
export const KIND_ORDER: EntityKind[] = [
  'goal',
  'workstream',
  'story',
  'change',
  'dailyUpdate',
  'blocker',
  'document',
  'decision',
  'service',
  'estimate',
];

/**
 * Returns refs whose timestamp falls within [fromIso, toIso]. Refs without a
 * parseable timestamp are excluded (they cannot be placed in a window).
 */
export function refsInRange(
  refs: EntityRef[],
  fromIso: string,
  toIso: string,
): EntityRef[] {
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  return refs.filter((r) => {
    const t = Date.parse(r.timestamp);
    if (Number.isNaN(t)) return false;
    return t >= from && t <= to;
  });
}

/** Sorts refs newest-first (by timestamp). */
export function sortByRecency(refs: EntityRef[]): EntityRef[] {
  return refs
    .slice()
    .sort((a, b) => (b.timestamp ?? '').localeCompare(a.timestamp ?? ''));
}

/**
 * Ranks refs by relevance to a free-text query (RAG-lite). Drops zero-score
 * refs; recency breaks ties; returns at most `limit` refs.
 */
export function rankRefs(
  refs: EntityRef[],
  query: string,
  limit = 24,
): EntityRef[] {
  if (!query.trim()) return sortByRecency(refs).slice(0, limit);
  return refs
    .map((r) => ({ r, s: scoreText(`${r.title} · ${r.text}`, query) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => {
      if (b.s !== a.s) return b.s - a.s;
      return (b.r.timestamp ?? '').localeCompare(a.r.timestamp ?? '');
    })
    .slice(0, limit)
    .map((x) => x.r);
}

export interface FormatContextOptions {
  /** Max refs emitted per kind (keeps the prompt bounded). Default 40. */
  perKindLimit?: number;
  /** Max characters of the text corpus per item. Default 220. */
  maxCharsPerItem?: number;
}

/**
 * Formats a set of refs into a compact, sectioned Markdown-ish context block
 * for the model. Grouped by kind (in {@link KIND_ORDER}), newest-first within a
 * group, each line carrying the entity id so the model can reference it. Bounded
 * per kind + per item so the prompt stays small regardless of team size.
 */
export function formatContext(
  refs: EntityRef[],
  options: FormatContextOptions = {},
): string {
  const perKindLimit = options.perKindLimit ?? 40;
  const maxChars = options.maxCharsPerItem ?? 220;
  if (refs.length === 0) return '(no entities in scope)';

  const byKind = new Map<EntityKind, EntityRef[]>();
  for (const r of refs) {
    const list = byKind.get(r.kind) ?? [];
    list.push(r);
    byKind.set(r.kind, list);
  }

  const sections: string[] = [];
  for (const kind of KIND_ORDER) {
    const list = byKind.get(kind);
    if (!list || list.length === 0) continue;
    const ordered = sortByRecency(list).slice(0, perKindLimit);
    const lines = ordered.map((r) => {
      const body = r.text.length > maxChars ? `${r.text.slice(0, maxChars)}…` : r.text;
      const who = r.people.length ? ` [people: ${r.people.slice(0, 6).join(', ')}]` : '';
      return `- (${r.id}) ${r.title}${body ? ` — ${body}` : ''}${who}`;
    });
    const omitted = list.length - ordered.length;
    const more = omitted > 0 ? `\n  …and ${omitted} more` : '';
    sections.push(`### ${kindLabel(kind)}s (${list.length})\n${lines.join('\n')}${more}`);
  }
  return sections.join('\n\n');
}
